'use strict';
(function () {
  if (window.VSKLOCartCore) return;

  function create(options) {
    options = options || {};
    var root = options.root;
    var isAddon = options.isAddon || function () { return false; };
    var itemGroup = options.itemGroup || function () { return ''; };
    var route = options.route;
    var fetchConfigJson = options.fetchConfigJson;
    var queuedFetch = options.queuedFetch;
    var cartMutation = options.cartMutation;
    var updateCartDomFromState = options.updateCartDomFromState;
    var refreshPriceBlocks = options.refreshPriceBlocks;
    var refreshCartPage = options.refreshCartPage;
    var publishCartState = options.publishCartState;
    var saveAddonText = options.saveAddonText;
    var message = options.message || function () {};
    var t = options.t || function (_key, fallback) { return fallback; };

    if (!root || typeof route !== 'function' || typeof fetchConfigJson !== 'function' ||
        typeof queuedFetch !== 'function' || typeof updateCartDomFromState !== 'function' ||
        typeof publishCartState !== 'function' || typeof saveAddonText !== 'function' ||
        typeof cartMutation !== 'function') {
      throw new Error('[VSKLO] cart core dependencies are incomplete');
    }

    var pending = new Map();
    var pendingHolders = new Set();
    var autosaveTimers = new Map();
    var addonSaveChain = Promise.resolve();
    var quantityFlushTimer = null;
    var quantityFlushChain = Promise.resolve();
    var cartClearing = false;

    function holderCartKey(holder) {
      if (!holder) return '';
      if (holder.dataset && holder.dataset.cartKey) return String(holder.dataset.cartKey);
      var qty = holder.querySelector ? holder.querySelector('[data-vsklo-qty]') : null;
      return String(qty && qty.dataset ? qty.dataset.key || '' : '');
    }

    function discardPendingQuantityKeys(keys) {
      var drop = new Set(
        (keys || []).map(function (key) { return String(key || ''); }).filter(Boolean)
      );
      if (!drop.size) return;

      drop.forEach(function (key) { pending.delete(key); });
      Array.from(pendingHolders).forEach(function (holder) {
        if (!drop.has(holderCartKey(holder))) return;
        if (holder && holder.dataset) delete holder.dataset.vskloQtyPending;
        pendingHolders.delete(holder);
      });
    }

    function cancelAutosaveForRows(rows) {
      (rows || []).forEach(function (row) {
        var key = String(row && row.dataset ? row.dataset.cartKey || '' : '');
        if (!key) return;
        window.clearTimeout(autosaveTimers.get(key));
        autosaveTimers.delete(key);
      });
    }

    async function waitForAddonSaves(label) {
      await addonSaveChain.catch(function (error) {
        console.warn(label || 'VSKLO previous addon autosave failed', error);
      });
    }

    async function prepareStructuralRemoval(scope) {
      var rows = scope
        ? Array.from(
            scope.matches && scope.matches('[data-vsklo-addon-row]')
              ? [scope]
              : (scope.querySelectorAll ? scope.querySelectorAll('[data-vsklo-addon-row]') : [])
          )
        : [];

      cancelAutosaveForRows(rows);

      await waitForAddonSaves('VSKLO addon autosave did not finish before structural removal');

      if (
        window.VSKLOCartMutationQueue &&
        typeof window.VSKLOCartMutationQueue.whenIdle === 'function'
      ) {
        await window.VSKLOCartMutationQueue.whenIdle();
      }

      var keys = [];
      if (scope && scope.dataset && scope.dataset.cartKey) keys.push(scope.dataset.cartKey);
      rows.forEach(function (row) {
        if (row.dataset && row.dataset.cartKey) keys.push(row.dataset.cartKey);
      });
      discardPendingQuantityKeys(keys);

      await flushPendingQuantityUpdates();
    }

    async function discardAllPendingCartEdits() {
      window.clearTimeout(quantityFlushTimer);
      quantityFlushTimer = null;

      await quantityFlushChain.catch(function (error) {
        console.warn('VSKLO quantity sync superseded by clear cart', error);
      });

      pending.clear();
      pendingHolders.forEach(function (holder) {
        if (holder && holder.dataset) delete holder.dataset.vskloQtyPending;
      });
      pendingHolders.clear();

      autosaveTimers.forEach(function (timer) { window.clearTimeout(timer); });
      autosaveTimers.clear();

      await waitForAddonSaves('VSKLO addon autosave superseded by clear cart');
    }

    function scheduleAddonAutosave(row, delay) {
      if (!row || row.dataset.noEngraving === 'true') return;
      var key = row.dataset.cartKey;
      if (!key) return;

      window.clearTimeout(autosaveTimers.get(key));
      autosaveTimers.set(
        key,
        window.setTimeout(function () {
          addonSaveChain = addonSaveChain
            .then(function () {
              return saveAddonText(row, { silent: true });
            })
            .catch(function (error) {
              if (row && row.dataset) row.dataset.vskloDirty = '1';
              console.error('VSKLO serialized autosave failed', error);
              return null;
            });
        }, typeof delay === 'number' ? delay : 800)
      );
    }

    function scheduleQuantityFlush(delay) {
      window.clearTimeout(quantityFlushTimer);
      quantityFlushTimer = window.setTimeout(function () {
        quantityFlushTimer = null;
        quantityFlushChain = quantityFlushChain
          .then(function () { return flushQuantityBatch(false); })
          .catch(function (error) {
            console.error('VSKLO quantity batch update failed', error);
            message(error?.vskloCartValidation
              ? error.message
              : t('quantityUpdateError', 'Не вдалося зберегти кількість. Спробуйте ще раз.'));
          });
      }, typeof delay === 'number' ? delay : 520);
    }

    function queueQuantity(key, quantity, holder) {
      if (!key) return;
      var normalizedQty = Math.max(0, parseInt(quantity == null ? 1 : quantity, 10) || 0);
      pending.set(key, normalizedQty);

      if (holder) {
        holder.dataset.vskloQtyPending = '1';
        pendingHolders.add(holder);
      }

      scheduleQuantityFlush(520);
    }

    // Discount/promotion engines can split one variant into several Shopify
    // lines or change a line key. Updating an old card by exact key would remove
    // its markup while cart.js still contains the item. Always reconcile the
    // server section when the rendered key set no longer matches Shopify.
    function cartStructureChanged(cart) {
      var rendered = Array.from(root.querySelectorAll(
        '[data-vsklo-parent-card], [data-vsklo-addon-row], [data-vsklo-orphan]'
      )).map(function (node) { return String(node.dataset.cartKey || ''); }).filter(Boolean);
      var incoming = (cart.items || [])
        .map(function (item) { return String(item.key || ''); }).filter(Boolean);
      if (rendered.length !== incoming.length) return true;
      var keys = new Set(incoming);
      if (rendered.some(function (key) { return !keys.has(key); })) return true;

      // The canonical service owner may change after a quantity update
      // without any new Shopify line keys. Re-render grouping if it does.
      var ownerByGroup = new Map();
      (cart.items || []).forEach(function (item) {
        if (isAddon(item)) return;
        var group = String(itemGroup(item) || '');
        if (!group) return;
        var prior = ownerByGroup.get(group);
        if (!prior || Number(item.quantity) > Number(prior.quantity)) {
          ownerByGroup.set(group, item);
        }
      });
      return Array.from(root.querySelectorAll(
        '[data-vsklo-parent-card][data-vsklo-group-owner]'
      )).some(function (card) {
        var owner = ownerByGroup.get(card.dataset.vskloGroupId || '');
        if (!owner) return false;
        return (card.dataset.vskloGroupOwner === 'true') !==
          (String(owner.key) === String(card.dataset.cartKey));
      });
    }

    // Independently purchased services can exceed the number of sets.
    // Preserve stale-key protection; do not cap engraving/service quantities.
    function validateGroupQuantities(cart, updates) {
      if (!Array.isArray(cart?.items)) throw new Error('Invalid cart quantity preflight');
      const keys = new Set(cart.items.map(item => String(item.key || '')));
      Object.keys(updates).forEach(key => {
        if (keys.has(String(key))) return;
        const error = new Error(t('cartChangedDuringEdit', 'Кошик оновився під час редагування. Оновіть сторінку та спробуйте ще раз.'));
        error.vskloCartValidation = true;
        throw error;
      });
      return true;
    }

    async function flushQuantityBatch(forceDomSync) {
      if (!pending.size) return null;

      var batch = new Map(pending);
      var updates = {};
      batch.forEach(function (qty, key) {
        updates[key] = Math.max(0, parseInt(qty == null ? 1 : qty, 10) || 0);
      });
      pending.clear();

      try {
        var needsGroupPreflight = Array.from(root.querySelectorAll('[data-vsklo-qty]')).some(function (box) {
          if (!Object.prototype.hasOwnProperty.call(updates, String(box.dataset.key || ''))) return false;
          var holder = box.closest('[data-vsklo-parent-card], [data-vsklo-addon-row]');
          return !!String(holder?.dataset?.vskloGroupId || '');
        });
        var res = needsGroupPreflight
          ? await cartMutation('quantity-update-preflight', async function () {
              var snapshotResponse = await fetch(route('cart.js'), {
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
              });
              if (!snapshotResponse.ok) throw new Error('Cart quantity preflight unavailable');
              var snapshot = await snapshotResponse.json();
              validateGroupQuantities(snapshot, updates);
              return fetch(route('cart/update.js'), fetchConfigJson({ updates: updates }));
            })
          : await queuedFetch(
              'quantity-update',
              route('cart/update.js'),
              fetchConfigJson({ updates: updates })
            );
        var cart = await res.json();
        if (!res.ok || cart.errors) {
          throw new Error(cart.errors || cart.description || 'Cart update failed');
        }

        if (forceDomSync || !pending.size) {
          if (cartStructureChanged(cart)) {
            // Never delete cards on stale keys before the replacement HTML arrives.
            publishCartState(cart);
            root.setAttribute('aria-busy', 'true');
            try {
              if (typeof refreshCartPage !== 'function') {
                throw new Error('Cart authoritative section refresh is unavailable');
              }
              await refreshCartPage();
            } catch (error) {
              console.warn('[VSKLO] quantity structure reconciliation failed, reloading', error);
              window.location.reload();
            } finally {
              root.removeAttribute('aria-busy');
            }
          } else {
            updateCartDomFromState(cart);
            // An automatic Shopify Function discount can become eligible at
            // quantity 2 while cart.total_discount is still 0 in the response.
            // Do not gate the authoritative Liquid price/badge update on the
            // previous discount or fire-and-forget it after the quantity write.
            if (typeof refreshPriceBlocks === 'function') {
              try {
                await refreshPriceBlocks();
              } catch (error) {
                console.warn('[VSKLO] authoritative discount price refresh failed', error);
                // Avoid hammering Shopify if it explicitly rate-limited us.
                if (!/\b429\b/.test(String(error)) && typeof refreshCartPage === 'function') {
                  try {
                    await refreshCartPage();
                  } catch (fallbackError) {
                    console.warn('[VSKLO] cart discount reconciliation fallback failed', fallbackError);
                  }
                }
              }
            }
          }
          pendingHolders.forEach(function (holder) {
            if (holder && holder.dataset) delete holder.dataset.vskloQtyPending;
          });
          pendingHolders.clear();
        } else {
          publishCartState(cart);
        }

        return cart;
      } catch (error) {
        if (error?.vskloCartValidation) {
          // Refused validation did not write to Shopify. Restore the actual
          // server markup and avoid re-queuing an invalid quantity forever.
          pendingHolders.forEach(function (holder) {
            if (holder?.dataset) delete holder.dataset.vskloQtyPending;
          });
          pendingHolders.clear();
          if (typeof refreshCartPage === 'function') {
            try { await refreshCartPage(); }
            catch (refreshError) { console.warn('[VSKLO] quantity rollback view unavailable', refreshError); }
          }
        } else {
          batch.forEach(function (qty, key) {
            if (!pending.has(key)) pending.set(key, qty);
          });
        }
        throw error;
      }
    }

    async function flushPendingQuantityUpdates() {
      window.clearTimeout(quantityFlushTimer);
      quantityFlushTimer = null;

      await quantityFlushChain.catch(function (error) {
        console.warn('VSKLO previous quantity flush failed before checkout', error);
      });

      if (!pending.size) return null;
      return flushQuantityBatch(true);
    }

    async function flushDirtyAddonTextRows() {
      var rows = Array.from(
        root.querySelectorAll('[data-vsklo-addon-row][data-vsklo-dirty="1"]')
      );
      if (!rows.length) return;

      for (var index = 0; index < rows.length; index += 1) {
        var row = rows[index];
        var key = row.dataset.cartKey;
        if (key) {
          window.clearTimeout(autosaveTimers.get(key));
          autosaveTimers.delete(key);
        }
        await saveAddonText(row, { silent: true });
      }
    }

    async function beforeCheckout() {
      var hasQtyUpdates = pending.size > 0;
      var hasDirtyText = !!root.querySelector(
        '[data-vsklo-addon-row][data-vsklo-dirty="1"]'
      );

      if (!hasQtyUpdates && !hasDirtyText) return;

      await flushPendingQuantityUpdates();

      if (hasDirtyText) {
        await waitForAddonSaves(
          'VSKLO previous addon autosave failed; retrying before checkout'
        );
        await flushDirtyAddonTextRows();
      }
    }

    function hasPendingChanges() {
      return (
        pending.size > 0 ||
        !!root.querySelector('[data-vsklo-addon-row][data-vsklo-dirty="1"]')
      );
    }

    function setClearing(value) {
      cartClearing = Boolean(value);
    }

    function isClearing() {
      return cartClearing;
    }

    var api = Object.freeze({
      queueQuantity: queueQuantity,
      flushPendingQuantityUpdates: flushPendingQuantityUpdates,
      flushDirtyAddonTextRows: flushDirtyAddonTextRows,
      scheduleAddonAutosave: scheduleAddonAutosave,
      prepareStructuralRemoval: prepareStructuralRemoval,
      discardAllPendingCartEdits: discardAllPendingCartEdits,
      waitForAddonSaves: waitForAddonSaves,
      beforeCheckout: beforeCheckout,
      hasPendingChanges: hasPendingChanges,
      setClearing: setClearing,
      isClearing: isClearing
    });

    window.VSKLOCartBeforeCheckout = beforeCheckout;
    window.VSKLOCartHasPendingChanges = hasPendingChanges;

    return api;
  }

  window.VSKLOCartCore = Object.freeze({
    create: create
  });
})();